import { useState, useEffect, useRef } from "react";

export default function WhyChooseUs() {
    const [sectionProgress, setSectionProgress] = useState(0);
    const [isAnimated, setIsAnimated] = useState(false);
    const [isFullyAppeared, setIsFullyAppeared] = useState(false);
    const [activeItem, setActiveItem] = useState(null);
    const sectionRef = useRef(null);
    const animationFrameRef = useRef(null);

    // Update animation based on scroll position
    const updateAnimation = () => {
        if (!sectionRef.current) return;

        const section = sectionRef.current;
        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;
        const windowTop = window.scrollY;
        const windowHeight = window.innerHeight;

        // Calculate how much of the section is visible
        const sectionVisible = windowTop + windowHeight - sectionTop;
        const progress = Math.max(
            0,
            Math.min(1, sectionVisible / (sectionHeight * 0.6))
        );

        setSectionProgress(progress);

        if (progress >= 1) {
            setIsAnimated(true);
            // Check if section is fully appeared with full width
            const currentMaxWidth = 500 + progress * (window.innerWidth - 500);
            const isFullWidth = currentMaxWidth >= window.innerWidth * 0.95; // 95% of viewport width
            setIsFullyAppeared(isFullWidth);
        } else {
            setIsAnimated(false);
            setIsFullyAppeared(false);
        }
    };

    // Throttled scroll handler
    const throttledUpdate = () => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }
        animationFrameRef.current = requestAnimationFrame(updateAnimation);
    };

    useEffect(() => {
        window.addEventListener("scroll", throttledUpdate);

        // Initial check
        updateAnimation();

        return () => {
            window.removeEventListener("scroll", throttledUpdate);
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    // Calculate dynamic styles
    const getDynamicStyles = () => {
        if (typeof window === "undefined") {
            return {
                maxWidth: "500px",
                borderRadius: "40px",
                transform: "scale(0.8)",
                opacity: 0.3,
                transition: "all 0.1s ease-out",
            };
        }

        const currentMaxWidth = 500 + sectionProgress * (window.innerWidth - 500);
        const currentBorderRadius = 40 - sectionProgress * 40;
        const currentScale = 0.8 + sectionProgress * 0.2;
        const currentOpacity = 0.3 + sectionProgress * 0.7;

        return {
            maxWidth: `${currentMaxWidth}px`,
            borderRadius: `${currentBorderRadius}px`,
            transform: `scale(${currentScale})`,
            opacity: currentOpacity,
            transition: "all 0.1s ease-out",
        };
    };

    // Accordion toggle
    const handleItemClick = (itemId) => {
        if (activeItem === itemId) {
            setActiveItem(null);
        } else {
            setActiveItem(itemId);
        }
    };

    // Course data
    const whyChooseItems = [
        { id: 1, title: "Expert Faculty", description: "Certified professionals & celebrity artists." },
        { id: 2, title: "Premier Curriculum", description: "Internationally structured and exam-focused." },
        { id: 3, title: "Performance Opportunities", description: "Stage shows, concerts & studio training." },
        { id: 4, title: "Personalized Learning", description: "Flexible, one-to-one attention." },
        { id: 5, title: "Global Certifications", description: "Trinity, Rock School, ABRSM, Bhatkhande." },
        { id: 6, title: "Premium Learning Environment", description: "Online & offline with world-class setup." },
        { id: 7, title: "Multi-Genre Training", description: "Western, Bollywood, Classical, Opera & more." },
        { id: 8, title: "Customised Programs", description: "Tailored learning paths for every student." },
        { id: 9, title: "Career Pathways", description: "Guidance for professional musicians & performers." },
        { id: 10, title: "Proven Results", description: "Successful students across India & abroad." },
        { id: 11, title: "Progress Tracking", description: "Regular assessments to monitor student growth." },
        { id: 12, title: "Strong Track Record", description: "1000+ students trained with consistent excellence." },
    ];

    return (
        <section
            id="why-choose-us-section"
            ref={sectionRef}
            className={isAnimated ? "animate" : ""}
            style={getDynamicStyles()}
        >
            <div className="why-choose-container">
                <div className="why-choose-content">
                    <div className="why-choose-left">
                        <h2 className="section-title">Why Choose Us</h2>
                    </div>

                    <div className="why-choose-right">
                        <div className="why-choose-list">
                            {whyChooseItems.map((item) => (
                                <div
                                    key={item.id}
                                    className={`why-choose-item ${activeItem === item.id ? "active" : ""
                                        }`}
                                    data-why={item.id}
                                    onClick={() => handleItemClick(item.id)}
                                >
                                    <div className="why-choose-question">
                                        <span className="item-text">{item.title}</span>
                                        <div className="item-arrow">
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                            >
                                                <path d="M6 9l6 6 6-6" />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="item-answer">
                                        <p>{item.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
